import { useEffect, useState } from 'react'
import { api } from '../services/api'
import { storePaymentOptions } from '../lib/storePayment'
import type { Store } from '../store/useStore'

/**
 * #1178 店舗の会計案内スタイルを取得する共有フック。
 *
 * CheckoutSheet / MyOrders は従来 zustand の stores リストから
 * terminal/checkoutStyle を読んでいたが、リストはアプリ起動時のスナップ
 * ショットで、店側の設定変更が反映されない (FB: staff_brings に設定したのに
 * 旧文言が出る)。表示のたびに store doc を直接取得して確実に最新を使う。
 * モジュールキャッシュを初期値に使い、表示のチラつきを抑える。
 */

export interface CheckoutStyleInfo {
  /** レジでカードが使える (S700 を登録済み、または #2162 お店の決済端末を使う店) */
  payAtRegister: boolean
  /** 会計伝票をスタッフが席へ持参する店 */
  staffBrings: boolean
  /** #1801 アプリ内カード決済を受けられる店か (現金のみ運用 / KYC で決まる。lib/storePayment.ts) */
  cardInApp: boolean
  /** #1800 YOKOSO ポイントで払える店か (店が受ける設定 + KYC。現金のみ運用でも可) */
  points: boolean
}

const cache = new Map<string, CheckoutStyleInfo>()

export function useCheckoutStyle(storeId: string | null | undefined, active: boolean = true) {
  const [info, setInfo] = useState<CheckoutStyleInfo | null>(
    storeId ? cache.get(storeId) ?? null : null,
  )

  useEffect(() => {
    if (!storeId || !active) return
    let cancelled = false
    api.getStore(storeId)
      .then((s: { terminal?: { readerId?: string } | null; checkoutStyle?: string | null } & Partial<Store>) => {
        const options = storePaymentOptions(s as Store)
        const v: CheckoutStyleInfo = {
          payAtRegister: !!s.terminal?.readerId || s.storeTerminalEnabled === true,
          staffBrings: s.checkoutStyle === 'staff_brings',
          cardInApp: options.cardInApp,
          points: options.points,
        }
        cache.set(storeId, v)
        if (!cancelled) setInfo(v)
      })
      .catch((err) => {
        // キャッシュ or null のまま (既定文言で縮退)。黙って飲まず console に残す —
        // 500 が「現金のみ」の顔をして出ないように、null のときは CheckoutSheet が
        // 支払手段を出さない
        console.error('[useCheckoutStyle] store fetch failed:', err)
      })
    return () => { cancelled = true }
  }, [storeId, active])

  return info
}
